import {useState} from 'react'
import {useSearchParams} from 'react-router'
import DeleteAccountFlow from '@/customComponents/accountDeletion/DeleteAccountFlow.jsx'

/**
 * THE PUBLIC DELETION PAGE — `/delete-account`, no session required.
 *
 * ── ⚠️ IT EXISTS BECAUSE GOOGLE PLAY REQUIRES IT ──────────────────────────────────
 * The rule is that a user must be able to delete their account WITHOUT installing the app.
 * So this page cannot sit behind `ProtectedRoute`, cannot assume a token, and cannot
 * redirect to sign-in — a person who has lost their phone, or never had the app, has to be
 * able to finish here. It is registered in Play Console, so the URL is part of a
 * submission and should not be renamed casually.
 *
 * ── ⚠️ ONE PAGE, BOTH APPS, BECAUSE THE ENDPOINTS NEVER CROSS ─────────────────────
 * `/passengers/...` deletes passengers and `/drivers/...` deletes drivers, and the wrong
 * one fails SILENTLY: step 1 is enumeration-safe, so a driver who picks "Hopn" is told a
 * code has been sent and then waits forever. That is the whole reason the toggle is the
 * first thing on the page and is worded by APP NAME rather than by role — "Hopn" and "Hopn
 * Navigator" are what people have installed; "passenger" and "driver" are our words.
 *
 * ⚠️ `?app=` PRESELECTS IT AND NOTHING MORE. The marketing site links here with the
 * parameter set, but a person who arrives without one, or with a typo in it, still gets a
 * working page with a visible choice rather than a silent default.
 */

const APPS = {
    hopn: {leg: 'passenger', label: 'Hopn', sub: 'Passenger app'},
    'hopn-navigator': {leg: 'driver', label: 'Hopn Navigator', sub: 'Driver app'},
}

export default function DeleteAccountPage() {
    const [params] = useSearchParams()
    const requested = params.get('app')
    const [appKey, setAppKey] = useState(APPS[requested] ? requested : null)

    const chosen = APPS[appKey] || null

    return (
        /* Its own shell — no sidebar, no header, no account menu. Everything in the
           dashboard chrome assumes a session this page deliberately does not have. */
        <div className="mx-auto flex min-h-dvh w-full max-w-xl flex-col justify-center gap-6 px-4 py-10">
            <header className="text-center">
                <h2 className="mb-2!">Delete your account</h2>
                <p className="mb-0! text-(--text-secondary)">
                    You can delete your Hopn account here without installing the app.
                </p>
            </header>

            <div className="white-global-section flex flex-col gap-5">
                {/* ⚠️ THE CHOICE CANNOT BE CHANGED MID-FLOW. A code is issued against one
                    leg; switching afterwards would leave a valid code for an account the
                    form is no longer talking to, and the confirm would fail with "invalid
                    code" for a reason nothing on screen explains. Starting over is one
                    reload and is the honest reset. */}
                {!chosen ? (
                    <>
                        <p className="mb-0! font-medium text-(--text-primary)">
                            Which app do you have an account with?
                        </p>
                        <div className="flex flex-col gap-3">
                            {Object.entries(APPS).map(([key, app]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setAppKey(key)}
                                    className="flex w-full cursor-pointer items-center justify-between gap-4 rounded-(--radius-lg) border border-(--border-color) bg-(--bg-white) p-4 text-left hover:opacity-80"
                                >
                                    <span className="min-w-0">
                                        <span className="block font-bold text-(--text-primary)">{app.label}</span>
                                        <span className="block text-sm text-(--text-secondary)">{app.sub}</span>
                                    </span>
                                    <span aria-hidden="true" className="text-(--text-secondary)">›</span>
                                </button>
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <p className="mb-0! text-sm text-(--text-secondary)">
                            Deleting your <strong className="text-(--text-primary)">{chosen.label}</strong> account
                        </p>
                        <DeleteAccountFlow
                            leg={chosen.leg}
                            /* ⚠️ NO `onDeleted`. There is nowhere to send someone who was never
                               signed in — the success screen with the reference IS the end of
                               the journey here. In the app it signs them out instead. */
                            onCancel={() => setAppKey(null)}
                        />
                    </>
                )}
            </div>
        </div>
    )
}

export {DeleteAccountPage}
